import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getImageUrl } from "../../../helpers/imageUrl";

const shellStyle = {
  position: "relative",
  width: "100%",
  aspectRatio: "343 / 123",
  minHeight: "clamp(110px, 34vw, 140px)",
  borderRadius: "8px",
  overflow: "hidden",
  boxSizing: "border-box",
};

const PaginationDots = ({ count, activeIndex, onSelect }) => {
  if (count <= 1) return null;

  return (
    <div
      role="tablist"
      aria-label="Promo banner slides"
      style={{
        position: "absolute",
        left: "50%",
        bottom: "clamp(4px, 1.5vw, 8px)",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: "3px",
        zIndex: 3,
      }}
    >
      {Array.from({ length: count }, (_, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={index === activeIndex}
          aria-label={`Slide ${index + 1}`}
          onClick={() => onSelect(index)}
          style={{
            width: "10px",
            height: "4px",
            padding: 0,
            border: "none",
            borderRadius: "18px",
            background: index === activeIndex ? "#ffffff" : "#995190",
            cursor: "pointer",
          }}
        />
      ))}
    </div>
  );
};

const SlideLink = ({ slide, children }) => {
  const href = slide?.url;
  const isExternal = slide?.data?.model === "3";

  if (!href) {
    return <div style={{ width: "100%", height: "100%" }}>{children}</div>;
  }

  if (isExternal) {
    return (
      <a href={href} style={{ display: "block", width: "100%", height: "100%", textDecoration: "none" }}>
        {children}
      </a>
    );
  }

  return (
    <Link to={`../${href}`} style={{ display: "block", width: "100%", height: "100%", textDecoration: "none" }}>
      {children}
    </Link>
  );
};

const DynamicPromoBanner = ({ slides, current, onSelectDot }) => (
  <div style={shellStyle}>
    {slides.map((slide, index) => (
      <div
        key={slide.data?.id || index}
        style={{
          position: "absolute",
          inset: 0,
          opacity: index === current ? 1 : 0,
          pointerEvents: index === current ? "auto" : "none",
          transition: "opacity 0.35s ease",
        }}
      >
        <SlideLink slide={slide}>
          <img
            src={getImageUrl(slide.data?.image)}
            alt={slide.data?.name || "Promo banner"}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
            }}
          />
        </SlideLink>
      </div>
    ))}
    <PaginationDots count={slides.length} activeIndex={current} onSelect={onSelectDot} />
  </div>
);

/**
 * Home promo banner from main-position Promo Slider slides.
 * Renders nothing when there are no slides.
 */
const HomePromoBanner = ({ slides = [], autoPlayMs = 5000 }) => {
  const hasSlides = Array.isArray(slides) && slides.length > 0;
  const [current, setCurrent] = useState(0);

  const goTo = useCallback((index) => {
    setCurrent(index);
  }, []);

  useEffect(() => {
    setCurrent(0);
  }, [slides]);

  useEffect(() => {
    if (!hasSlides || slides.length <= 1) return undefined;
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, autoPlayMs);
    return () => clearInterval(id);
  }, [hasSlides, slides, autoPlayMs]);

  if (!hasSlides) return null;

  return <DynamicPromoBanner slides={slides} current={current} onSelectDot={goTo} />;
};

export default HomePromoBanner;
